import React, { useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, ListItem, Menu, Tooltip, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import LanguageIcon from '@mui/icons-material/Language';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import { colors } from '../../../styles/theme';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../ActionMenuItem';
import {
  canEditCadCollection,
  canManageCadProject,
  canPublishCadProject,
  getCadShareBadgeType,
} from '../../../Showroom/utils/cadAccess';
import { formatCadCollectionIndexMeta } from '../../../Showroom/utils/cadParcelDisplay';
import {
  getListItemRootSx,
  getListItemCountTypographySx,
  getListRowSurfaceSx,
  getListVisibilityBadgeSx,
  listCardActionIconSx,
  listCardMetaRowSx,
  listNameTypographySx,
  listRowPreviewActiveSx,
  rowActionsBoxSx,
  viewRowBoxSx,
  viewRowTitleRowSx,
} from './UserLotListsPanel.styles';

/**
 * Card de showroom CAD en el índice de listas.
 * Click (propio o invitado) abre el detalle in-panel. El invitado ve
 * solo lectura salvo `can_edit`. Compartir = dueño (`can_publish`).
 * Espejo de espacio (Mis listas del owner, `guest=false`): sin flags de
 * dueño/editor no hay Actividad/Consultas. Eliminar pide `can_publish` y nunca
 * con `spaceMirror` (solo dueño). Compartir en espejo (F3-CAD) va con
 * `spaceCanShare` (el panel lo prende si hay escritura en el espacio; el
 * diálogo confirma `can_share` con el GET). Ver docs/maps/espacios-invite.md.
 */
export default function CadCollectionListRow({
  project,
  isLast = false,
  guest = false,
  spaceMirror = false,
  spaceCanShare = false,
  onOpen,
  onShare,
  onActivity,
  onInquiries,
  onLeave,
  onDelete,
  actioning = false,
  deleting = false,
  onLocate,
  locateActive = false,
  locating = false,
  canLocate = false,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const slug = project?.slug;
  const canShare = !guest && (spaceMirror
    ? spaceCanShare && Boolean(onShare)
    : canPublishCadProject(project));
  const hasCadInsightsAccess = canEditCadCollection(project)
    || (!guest && (canPublishCadProject(project) || canManageCadProject(project)));
  const canActivity = Boolean(onActivity) && hasCadInsightsAccess;
  const canInquiries = Boolean(onInquiries) && hasCadInsightsAccess;
  const canDelete = !guest && !spaceMirror && canPublishCadProject(project) && Boolean(onDelete);
  const hasRowMenu = canShare || canDelete || canActivity || canInquiries || guest;
  const shareBadge = guest ? 'none' : getCadShareBadgeType(project);
  const title = project?.name || slug || 'Lista';
  const metaLabel = formatCadCollectionIndexMeta(project, { guest });
  const showCanEditChip = guest && canEditCadCollection(project);

  const openInPanel = () => {
    if (!project) return;
    onOpen?.(project);
  };

  const handleTitleClick = openInPanel;

  return (
    <ListItem disablePadding sx={getListItemRootSx(isLast)}>
      <Box sx={{
        ...getListRowSurfaceSx(false),
        ...(locateActive ? listRowPreviewActiveSx : {}),
      }}>
        <Box sx={viewRowBoxSx}>
          <Box sx={viewRowTitleRowSx}>
            <Typography
              onClick={handleTitleClick}
              sx={{ ...listNameTypographySx, cursor: 'pointer' }}
            >
              {title}
            </Typography>
            <Box sx={rowActionsBoxSx}>
              <Tooltip
                title={
                  !canLocate
                    ? 'Esta lista es un plano; no está en el mapa país'
                    : locateActive
                      ? 'Ocultar del mapa'
                      : 'Ubicar en el mapa'
                }
                placement="top"
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!canLocate || locating) return;
                      onLocate?.(project);
                    }}
                    disabled={!canLocate || locating}
                    sx={{
                      color: locateActive && canLocate ? colors.primary : '#FFFFFF',
                      p: 0.5,
                      '&:hover': {
                        color: colors.primary,
                        bgcolor: 'rgba(255,255,255,0.06)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(171,171,171,0.35)',
                      },
                    }}
                    aria-label="Ubicar en el mapa"
                  >
                    {locating ? (
                      <CircularProgress size={16} sx={{ color: '#9CA3AF' }} />
                    ) : (
                      <MyLocationOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              {hasRowMenu ? (
                <>
                  <Tooltip title="Más acciones" placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setMenuAnchor(event.currentTarget);
                      }}
                      sx={listCardActionIconSx}
                      aria-label="Más acciones"
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={ACTION_MENU_PAPER_PROPS}
                    MenuListProps={ACTION_MENU_LIST_PROPS}
                  >
                    {canActivity ? (
                      <ActionMenuItem
                        onClick={() => { setMenuAnchor(null); onActivity?.(project); }}
                        icon={<HistoryOutlinedIcon sx={{ fontSize: 20 }} />}
                        label="Historial"
                      />
                    ) : null}
                    {canInquiries ? (
                      <ActionMenuItem
                        onClick={() => { setMenuAnchor(null); onInquiries?.(project); }}
                        icon={<MailOutlineIcon sx={{ fontSize: 20 }} />}
                        label="Consultas"
                      />
                    ) : null}
                    {canShare ? (
                      <ActionMenuItem
                        onClick={() => { setMenuAnchor(null); onShare?.(project); }}
                        icon={<ShareIcon sx={{ fontSize: 20 }} />}
                        label="Compartir"
                      />
                    ) : null}
                    {canDelete ? (
                      <ActionMenuItem
                        onClick={() => { setMenuAnchor(null); onDelete?.(project); }}
                        disabled={deleting}
                        loading={deleting}
                        icon={<DeleteOutlineIcon sx={{ fontSize: 20 }} />}
                        label="Eliminar"
                      />
                    ) : null}
                    {guest ? (
                      <ActionMenuItem
                        onClick={() => { setMenuAnchor(null); onLeave?.(project); }}
                        disabled={actioning}
                        loading={actioning}
                        icon={<CloseIcon sx={{ fontSize: 20 }} />}
                        label="Dejar de ver"
                      />
                    ) : null}
                  </Menu>
                </>
              ) : null}
            </Box>
          </Box>

          <Box
            onClick={handleTitleClick}
            sx={{ ...listCardMetaRowSx, cursor: 'pointer' }}
          >
            <Typography sx={getListItemCountTypographySx()}>
              {metaLabel}
            </Typography>
            {showCanEditChip ? (
              <Chip
                size="small"
                label="Editor"
                sx={getListVisibilityBadgeSx('public')}
              />
            ) : null}
            {shareBadge === 'public' ? (
              <Chip
                size="small"
                icon={<LanguageIcon />}
                label="Público"
                sx={getListVisibilityBadgeSx('public')}
              />
            ) : null}
            {shareBadge === 'restricted' ? (
              <Chip
                size="small"
                icon={<LockOutlinedIcon />}
                label="Restringido"
                sx={getListVisibilityBadgeSx('private')}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </ListItem>
  );
}
